"use client";

import { BirthDateField } from "@components/commerce/baseForms/BirthDateField";
import CountryCodeField from "@components/commerce/baseForms/CountryCodeField";
import NameField from "@components/commerce/baseForms/NameField";
import { GenderField } from "@components/commerce/baseForms/GenderField";
import { SimpleSubmitButton } from "@components/commerce/baseForms/SimpleSubmitButton";
import { getCheckoutForSite, activeSiteCountry } from "@config/site/site-config";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getPersonalDetailsValidationSchema } from "@lib/validators/checkout/contactDetailsValidationSchema";
import { useApplyLocalizedErrorMessages } from "@lib/validators/yupConfig";
import { useUpdateProfile } from "@store//hooks/customerHooks";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Form, Formik } from "formik";
import { Button } from "@ecco/ui/button";
import { isCountryWithReverseNameOrder } from "@lib/utils/address";
import { getDateFormat } from "@lib/utils/dateOfBirth";
import { Checkout, CommonCMS, commonReplacements } from "@lib/constants/contentful";
import { PhoneticNames } from "./components/phonetic-names/PhoneticNames";
import BaseInputField from "@components/commerce/baseForms/BaseInputField";
import { createDateString } from "@lib/auth/formatDate";
import { Genders } from "@lib/constants/genders";
import { useState } from "react";
import { formatNameForLocale } from "@lib/utils/commonUtility";

interface ProfileDetailsFormValues {
    countryCode: string;
    phone: string;
    Day: string;
    Month: string;
    Year: string;
    gender: string;
    firstName: string;
    lastName: string;
    phoneticFirstName: string | null;
    phoneticLastName: string | null;
    dateOfBirth: string;
}

export const ProfileDetailsForm = ({ data, globalErrorHandler, handleClose = () => {} }) => {
    const { get: getMicrocopy } = useMicrocopy();
    useApplyLocalizedErrorMessages();
    const { phoneNumber = "", gender = "" } = data;
    const [savedGender, setSavedGender] = useState(gender || "");
    const dateArray = data?.dateOfBirth ? data.dateOfBirth.split("-") : ["", "", ""];
    const { countryCode } = getCheckoutForSite;
    const { updateMyProfileMutation } = useUpdateProfile();
    const country = activeSiteCountry;
    const locale = activeSiteCountry;

    const originalValues = {
        countryCode,
        phone: phoneNumber,
        Day: dateArray[2],
        Month: dateArray[1],
        Year: dateArray[0],
        gender: gender,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneticFirstName: data.phoneticFirstName,
        phoneticLastName: data.phoneticLastName,
        dateOfBirth: "",
    };

    const initialValues = {
        ...originalValues,
        countryCode: `${countryCode}`,
    };

    const additionalHeaders = {
        reasonForPhone: getMicrocopy(CommonCMS.page, "checkout.step1.formPhone.reasonForInsert"),
        submitButton: getMicrocopy(CommonCMS.page, CommonCMS.button.submit),
        firstName: getMicrocopy(CommonCMS.page, "uiText.firstName"),
        lastName: getMicrocopy(CommonCMS.page, "uiText.lastName"),
        phoneticFirstName: getMicrocopy(CommonCMS.page, CommonCMS.phoneticFirstName),
        phoneticLastName: getMicrocopy(CommonCMS.page, CommonCMS.phoneticLastName),
        birthdate: getMicrocopy(CommonCMS.page, CommonCMS.profile.birthDateLabel),
        gender: getMicrocopy(CommonCMS.page, CommonCMS.gender.title),
        maleGender: getMicrocopy(CommonCMS.page, CommonCMS.gender.male),
        femaleGender: getMicrocopy(CommonCMS.page, CommonCMS.gender.female),
        neutralGender: getMicrocopy(CommonCMS.page, CommonCMS.gender.neutral),
        Day: getMicrocopy(CommonCMS.page, "Day"),
        Month: getMicrocopy(CommonCMS.page, "Month"),
        Year: getMicrocopy(CommonCMS.page, "Year"),
        DayPlaceholder: getMicrocopy(CommonCMS.page, "DayPlaceholder"),
        country: getMicrocopy(CommonCMS.page, Checkout.step1.formCountry.title),
        phone: getMicrocopy(CommonCMS.page, Checkout.step1.formPhone.title),
        MonthPlaceholder: getMicrocopy(CommonCMS.page, "MonthPlaceholder"),
        YearPlaceholder: getMicrocopy(CommonCMS.page, "YearPlaceholder"),
        cancelLabel: getMicrocopy(CommonCMS.page, "address.cancel.button"),
        errors: {
            firstName: getMicrocopy(CommonCMS.page, "Firstname.error"),
            lastName: getMicrocopy(CommonCMS.page, "Lastname.error"),
            requiredField: getMicrocopy(CommonCMS.page, Checkout.requiredFieldError),
            personalDetailsUpdateError: getMicrocopy(
                CommonCMS.page,
                "personaldetails.update.error"
            ),
            phoneRequirements: getMicrocopy(CommonCMS.page, Checkout.step1.formPhone.error),
            maxNameCharacters: getMicrocopy(CommonCMS.page, CommonCMS.error.maxCharacters, {
                replacements: commonReplacements.maxNameCharacters,
            }),
            maxPhoneCharacters: getMicrocopy(CommonCMS.page, CommonCMS.error.maxCharacters, {
                replacements: commonReplacements.maxPhoneCharacters,
            }),
            dateOfBirth: getMicrocopy(CommonCMS.page, "ErrorBirthdate"),
        },
    };

    return (
        <Formik<ProfileDetailsFormValues>
            validationSchema={getPersonalDetailsValidationSchema(additionalHeaders.errors, {
                firstName: additionalHeaders.firstName,
                lastName: additionalHeaders.lastName,
            })}
            initialValues={initialValues}
            onSubmit={async (values) => {
                const submittableValues = {
                    ...originalValues,
                    ...values,
                };
                const isSubmittable: boolean =
                    JSON.stringify(submittableValues) !== JSON.stringify(originalValues);
                if (!isSubmittable) {
                    return;
                }

                if (isSubmittable) {
                    const errorMessageHandler = (errorResponse) => {
                        if (!errorResponse) {
                            return;
                        }
                        globalErrorHandler(
                            errorResponse.message ||
                                additionalHeaders.errors.personalDetailsUpdateError
                        );
                    };
                    const { Day, Month, Year } = submittableValues;
                    const dateOfBirth = createDateString(Number(Day), Number(Month), Number(Year));
                    const vars = {
                        firstName: submittableValues.firstName
                            ? formatNameForLocale(submittableValues.firstName)
                            : submittableValues.firstName,
                        lastName: submittableValues.lastName
                            ? formatNameForLocale(submittableValues.lastName)
                            : submittableValues.lastName,
                        phoneticFirstName: submittableValues.phoneticFirstName,
                        phoneticLastName: submittableValues.phoneticLastName,
                        //SFAPI takes dateOfBirth as a string
                        dateOfBirth,
                        // HEAP takes each value as number and cannot be empty string
                        preferredLanguage: locale ?? null,
                        dayOfBirth: Day !== "" ? Number(Day) : null,
                        monthOfBirth: Month !== "" ? Number(Month) : null,
                        yearOfBirth: Year !== "" ? Number(Year) : null,
                        gender: savedGender,
                        phoneCountryCode: submittableValues.countryCode
                            ? submittableValues.countryCode
                            : null,
                        phoneNumber: submittableValues.phone ? submittableValues.phone : null,
                    };

                    try {
                        await updateMyProfileMutation.mutateAsync(vars as any).then(() => {
                            handleClose();
                        });
                    } catch (error) {
                        errorMessageHandler(error);
                    }
                }
            }}
        >
            {({ errors, touched, isSubmitting, setFieldValue }) => {
                return (
                    <Form noValidate={true} data-testid="profiledetailsform">
                        <Box w={{ base: "100%", md: "60%" }}>
                            {(isCountryWithReverseNameOrder(country)
                                ? ["lastName", "firstName"]
                                : ["firstName", "lastName"]
                            ).map((field) => (
                                <NameField
                                    key={field}
                                    id={field}
                                    header={additionalHeaders[field]}
                                    error={errors[field]}
                                    isInvalid={!!errors[field] && touched[field]}
                                />
                            ))}
                            <PhoneticNames
                                isInvalid={[
                                    Boolean(
                                        !!errors.phoneticFirstName && touched.phoneticFirstName
                                    ),
                                    Boolean(!!errors.phoneticLastName && touched.phoneticLastName),
                                ]}
                                errors={[
                                    errors.phoneticFirstName ?? "",
                                    errors.phoneticLastName ?? "",
                                ]}
                                country={country}
                                firstName={additionalHeaders.phoneticFirstName}
                                lastName={additionalHeaders.phoneticLastName}
                            />
                            <Box>
                                <Flex gap={5}>
                                    <CountryCodeField
                                        id="countryCode"
                                        isInvalid={
                                            (!!errors.countryCode && touched.countryCode) as boolean
                                        }
                                        header={additionalHeaders.country}
                                        error={errors.countryCode}
                                    />
                                    <BaseInputField
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        isInvalid={Boolean(!!errors.phone && touched.phone)}
                                        header={additionalHeaders.phone}
                                        error={errors.phone}
                                        helpText={additionalHeaders.reasonForPhone}
                                        autocomplete="nope"
                                    />
                                </Flex>
                            </Box>
                            <BirthDateField
                                dateFormat={getDateFormat(country)}
                                fieldValidity={{
                                    Day: (!!errors.Day && touched.Day) as boolean,
                                    Month: (!!errors.Month && touched.Month) as boolean,
                                    Year: (!!errors.Year && touched.Year) as boolean,
                                    dateOfBirth: !!errors.dateOfBirth as boolean,
                                }}
                                fieldLabels={{
                                    Day: additionalHeaders.Day,
                                    Month: additionalHeaders.Month,
                                    Year: additionalHeaders.Year,
                                }}
                                header={additionalHeaders.birthdate}
                                error={additionalHeaders.errors.dateOfBirth}
                                placeholders={{
                                    Day: additionalHeaders.DayPlaceholder,
                                    Month: additionalHeaders.MonthPlaceholder,
                                    Year: additionalHeaders.YearPlaceholder,
                                }}
                            />
                            <Box>
                                <GenderField
                                    header={additionalHeaders.gender}
                                    values={[
                                        {
                                            value: Genders.MALE,
                                            label: additionalHeaders.maleGender,
                                        },
                                        {
                                            value: Genders.FEMALE,
                                            label: additionalHeaders.femaleGender,
                                        },
                                        {
                                            value: Genders.UNKNOWN,
                                            label: additionalHeaders.neutralGender,
                                        },
                                    ]}
                                    setUpdateGender={(value) => {
                                        setFieldValue("gender", value);
                                        setSavedGender(value);
                                    }}
                                    defaultValue={savedGender}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Flex w="100%" flexDirection="row" mt={4}>
                                <Button
                                    variant={"tertiary"}
                                    onClick={handleClose}
                                    w="100%"
                                    maxW={{ base: "100%", md: "186px" }}
                                >
                                    {additionalHeaders.cancelLabel}
                                </Button>
                                <SimpleSubmitButton
                                    headerText={additionalHeaders.submitButton}
                                    isSubmitting={isSubmitting}
                                    isLocked={false}
                                />
                            </Flex>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ProfileDetailsForm;
